import { getContentIdFromPath } from '@databases/database-state';
import { DatabaseContentContextProvider } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import AppContent from '@databases/frontend/AppContent';
import AppWithContext from '@databases/frontend/AppWithContext';
import { perf } from '@databases/logs/performance';
import { appStore } from '@databases/redux/store';
import { setContext } from '@databases/utils/redux/context/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import React, { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

function getParentContentId() {
  try {
    // eslint-disable-next-line no-restricted-syntax
    const pathname = window.top && window.top.location.pathname;

    return pathname ? getContentIdFromPath(pathname) : null;
  } catch {
    // can't access window.top if cross-origin
    return null;
  }
}
type AppProps = {
  entryId: string | null;
  fieldId: string | null;
  referencing: boolean;
  savedViewId: string | null;
  unsavedViewId: string | null;
};

const App: React.FC<AppProps> = ({
  entryId,
  fieldId,
  referencing,
  savedViewId,
  unsavedViewId,
}) => {
  const {
    activationId,
    cloudId,
    contentId,
    contentReferenceId: referenceId,
    identity,
    spaceId,
    spaceKey,
    spaceName,
    title,
  } = useSnapshot(currentBridgeData).data;

  perf.timeLog('app-initialization', 'standalone-app-render');

  useEffect(() => {
    // Focus iframe to enable keyboard shortcuts
    window.focus();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(
        setContext({ space: { key: spaceKey }, userId: identity.accountId }),
      );
    });
  }, [spaceKey, identity]);

  const contextValue = useMemo(
    () => ({
      activationId,
      cloudId,
      contentId,
      entryId,
      fieldId,
      parentContentId: getParentContentId(),
      referenceId,
      referencing,
      savedViewId,
      setContentId: () => {},
      spaceId,
      spaceKey,
      spaceName,
      title,
      unsavedViewId,
    }),
    [
      activationId,
      cloudId,
      contentId,
      entryId,
      fieldId,
      referenceId,
      referencing,
      savedViewId,
      spaceId,
      spaceKey,
      spaceName,
      title,
      unsavedViewId,
    ],
  );

  return (
    <DatabaseContentContextProvider value={contextValue}>
      <AppWithContext aaid={identity.accountId}>
        <AppContent />
      </AppWithContext>
    </DatabaseContentContextProvider>
  );
};

export default React.memo(App);
